/* global globalThis */
/**
 * Sync portal is served under `/sync/` (see sync-provider-portal vite `base`).
 * Loop URLs omit that segment (e.g. `/dashboard`).
 * @see https://developer.zendesk.com/api-reference/widget-messaging/web/core/
 */

/** Zendesk may not invoke callbacks on invalid field ids — unblock UI after this. */
export const ZENDESK_METADATA_CALLBACK_TIMEOUT_MS = 2000;

/** `loginUser` clears tags/fields; defer re-apply until auth settles. */
export const ZENDESK_POST_LOGIN_METADATA_MS = 300;

const ZENDESK_PORTAL_SESSION_KEY = 'subscribili_zendesk_portal';

const LOOP_PORTAL_CONTEXT = {
  portal: 'loop',
  label: 'Loop',
  portalTag: 'portal_loop',
  portalShort: 'loop',
};

const SYNC_PORTAL_CONTEXT = {
  portal: 'sync',
  label: 'Sync',
  portalTag: 'portal_sync',
  portalShort: 'sync',
};

const DEFAULT_PORTAL_CONTEXT = {
  ...LOOP_PORTAL_CONTEXT,
  normalizedPath: '/',
  hostname: 'unknown',
};

function normalizePath(pathname) {
  return (pathname || '/').replace(/\/+/g, '/');
}

function getBrowserWindow() {
  return globalThis.window;
}

function isBrowserEnvironment() {
  return getBrowserWindow() !== undefined;
}

export function getZendesk() {
  const browserWindow = getBrowserWindow();
  if (browserWindow === undefined) {
    return undefined;
  }
  const { zE } = browserWindow;
  return typeof zE === 'function' ? zE : undefined;
}

function getLocationContext(pathname) {
  const browserWindow = getBrowserWindow();
  if (browserWindow === undefined) {
    return { normalizedPath: '/', hostname: 'unknown' };
  }
  return {
    normalizedPath: normalizePath(pathname || browserWindow.location.pathname),
    hostname: browserWindow.location.hostname,
  };
}

function getPortalContext(pathname) {
  if (!isBrowserEnvironment()) return DEFAULT_PORTAL_CONTEXT;

  const location = getLocationContext(pathname);
  const isSync = /^\/sync(\/|$)/.test(location.normalizedPath);

  return {
    ...location,
    ...(isSync ? SYNC_PORTAL_CONTEXT : LOOP_PORTAL_CONTEXT),
  };
}

/** JWT `name` — may appear in conversation title when Zendesk honors it. */
export function getDisplayName(baseName) {
  const raw =
    typeof baseName === 'string' && baseName.trim() ? baseName.trim() : 'User';
  return `${raw} · ${getPortalContext().label}`;
}

function getConversationTags(context = getPortalContext()) {
  const { normalizedPath, hostname, portalShort, portalTag } = context;
  const hostSlug =
    hostname
      .replace(/\W/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 48) || 'unknown';
  const pathPart = normalizedPath
    .replace(/^\//, '')
    .replaceAll('/', '_')
    .replace(/\W/g, '')
    .slice(0, 64);
  const pageTag = pathPart ? `page_${pathPart}` : 'page_home';

  return ['subscribili', portalShort, portalTag, `host_${hostSlug}`, pageTag];
}

export function waitForZendesk(maxAttempts = 80, intervalMs = 100) {
  return new Promise((resolve, reject) => {
    let n = 0;
    const tick = () => {
      if (getZendesk()) {
        resolve();
        return;
      }
      n += 1;
      if (n >= maxAttempts) {
        reject(new Error('Zendesk snippet did not initialize in time'));
        return;
      }
      globalThis.setTimeout(tick, intervalMs);
    };
    tick();
  });
}

function runZendeskCallbackWithTimeout(invoke, done) {
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    done?.();
  };
  const timeoutId = globalThis.setTimeout(
    finish,
    ZENDESK_METADATA_CALLBACK_TIMEOUT_MS,
  );
  try {
    invoke(() => {
      globalThis.clearTimeout(timeoutId);
      finish();
    });
  } catch (e) {
    globalThis.clearTimeout(timeoutId);
    console.warn('Zendesk metadata', e);
    finish();
  }
}

function setPortalConversationField(context, callback) {
  const portalFieldId = process.env.REACT_APP_ZENDESK_PORTAL_FIELD_ID?.trim();
  if (!portalFieldId) {
    callback?.();
    return;
  }
  const zE = getZendesk();
  if (!zE) {
    callback?.();
    return;
  }
  runZendeskCallbackWithTimeout((done) => {
    zE(
      'messenger:set',
      'conversationFields',
      [{ id: portalFieldId, value: context.portal }],
      done,
    );
  }, callback);
}

function getStoredZendeskPortal() {
  return getBrowserWindow()?.sessionStorage.getItem(ZENDESK_PORTAL_SESSION_KEY);
}

function setStoredZendeskPortal(portal) {
  getBrowserWindow()?.sessionStorage.setItem(ZENDESK_PORTAL_SESSION_KEY, portal);
}

function resetWidget(done) {
  const zE = getZendesk();
  if (!zE) {
    done?.();
    return;
  }
  try {
    zE('messenger', 'resetWidget', () => {
      applyMetadata(done);
    });
  } catch (e) {
    console.warn('Zendesk resetWidget', e);
    applyMetadata(done);
  }
}

/**
 * @returns {boolean} true if widget was reset (portal changed)
 */
export function syncPortal(pathname, done) {
  const { portal } = getPortalContext(pathname);
  const previous = getStoredZendeskPortal();
  setStoredZendeskPortal(portal);

  if (previous && previous !== portal && getZendesk()) {
    resetWidget(done);
    return true;
  }

  done?.();
  return false;
}

/** Tags + optional Provider portal custom field (before first message). */
export function applyMetadata(done) {
  const zE = getZendesk();
  if (!zE) {
    done?.();
    return;
  }

  const context = getPortalContext();
  const tags = getConversationTags(context);

  runZendeskCallbackWithTimeout((tagsDone) => {
    zE('messenger:set', 'conversationTags', tags, tagsDone);
  }, () => {
    setPortalConversationField(context, done);
  });
}
